import {
  PolfEventPokerStatusEnum,
  PolfEventStatusEnum,
} from '@polf/shared/codegen/types'
import { useEffect, useRef } from 'react'
import {
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom'

import {
  useCurrentPlayerPokerViewQuery,
  useCurrentPlayerQuery,
} from './__generated__/hooks'
import BottomNav, { type Phase } from './components/BottomNav'
import TopBar from './components/TopBar'
import UpdatePrompt from './components/UpdatePrompt'
import { useOnlineStatus } from './hooks/useOnlineStatus'
import { modeEyebrow } from './util/event-mode'

export type { Phase }

/**
 * Shell wrapping all signed-in player routes (`/s/:playerKey/*`).
 *
 * Polls currentPlayer and exposes it via React Router's Outlet context, so a
 * phase change reaches the player without a refresh. Phase derivation reads
 * BOTH the event status and the poker phase
 * status — usePokerPhase=true on a FINISHED event keeps GOLF tabs until the
 * poker phase actually transitions to SEATED/RUNNING. That avoids the
 * "the round is done but the dealer hasn't drawn yet" gap showing the wrong
 * tab set.
 */
export type CurrentPlayerCtx = NonNullable<
  ReturnType<typeof useCurrentPlayerQuery>['data']
>['currentPlayer']

const PlayerShell = () => {
  const location = useLocation()
  const { playerKey = '' } = useParams<{ playerKey: string }>()
  const online = useOnlineStatus()
  const { data, loading } = useCurrentPlayerQuery({
    fetchPolicy: 'cache-and-network',
    // Poll the event so a phase change (usePokerPhase flipping on, event
    // finishing) reaches the player without a manual refresh. This is what
    // un-skips the poker-view poll below, which then tracks the poker status.
    pollInterval: 15_000,
  })
  // Cheap poll just for the status pill / phase derivation. PokerSeat does
  // its own (faster) poll for the clock data.
  const { data: pokerData, error: pokerError } = useCurrentPlayerPokerViewQuery(
    {
      fetchPolicy: 'cache-and-network',
      pollInterval: 10_000,
      skip: !data?.currentPlayer?.polfEvent?.usePokerPhase,
    }
  )

  // Transition redirect: the moment golf flips to poker (dealer draws seats),
  // take the player to the poker home ONCE — even from the read-only golf
  // archive (scorecard/leaderboard), which otherwise stays reachable during
  // poker and so isn't caught by the deep-link redirect below. Mirrors the
  // `phase` derivation further down; kept here (before the guard returns) so
  // the effect's hook order is stable.
  const navigate = useNavigate()
  const evt = data?.currentPlayer?.polfEvent
  const livePokerStatus = pokerData?.currentPlayerPokerView?.status ?? null
  const livePhase: Phase =
    evt?.status === PolfEventStatusEnum.Finished &&
    evt?.usePokerPhase &&
    livePokerStatus !== null &&
    livePokerStatus !== PolfEventPokerStatusEnum.NotStarted
      ? 'POKER'
      : 'GOLF'
  const dataReady =
    !!data?.currentPlayer &&
    (!evt?.usePokerPhase || !!pokerData || !!pokerError)
  const prevPhaseRef = useRef<Phase | null>(null)
  useEffect(() => {
    if (!dataReady) return
    const prev = prevPhaseRef.current
    prevPhaseRef.current = livePhase
    // Only a real in-session GOLF→POKER change navigates; the first ready
    // render just records the phase (prev === null) so a fresh load into an
    // already-poker event doesn't yank the player off a deep-linked page.
    if (prev === 'GOLF' && livePhase === 'POKER') {
      navigate(`/s/${playerKey}/poker`, { replace: true })
    }
  }, [dataReady, livePhase, playerKey, navigate])

  if (loading && !data) {
    return (
      <main className="min-h-dvh flex items-center justify-center">
        <p className="text-ink-500">Laster…</p>
      </main>
    )
  }

  // Only a MISSING player is an invalid link. Now that currentPlayer polls, a
  // transient error (network blip, or a momentary backend race while the poker
  // phase is being enabled) must NOT blank the whole app — Apollo keeps the
  // last good `data` across a failed refetch, so we keep rendering it. Blanking
  // on `error` here flashed "Spillerlenke ikke gyldig" the instant poker was
  // turned on, even though the cached player was still valid.
  if (!data?.currentPlayer) {
    return (
      <main className="min-h-dvh flex flex-col items-center justify-center px-6 text-center gap-2">
        <h1 className="font-display text-xl text-flag">
          Spillerlenke ikke gyldig
        </h1>
        <p className="text-ink-500 text-sm">
          Be om en ny SMS-lenke fra arrangøren.
        </p>
      </main>
    )
  }

  // If the event has poker enabled we can't derive phase = POKER until the
  // poker query resolves (its `status` is what flips phase). Hold the shell on
  // the loader until poker data lands rather than rendering golf content.
  //
  // Gating on `!pokerData` (not `loading`) is deliberate: when the in-session
  // poll flips `usePokerPhase` on, the poker query un-skips and Apollo can
  // report `loading: false` for a tick before the fetch starts — in that tick
  // the golf Score page would flash (phase still GOLF, poker status unknown),
  // stranding a player on /score with the wrong banner/nav until refresh. This
  // makes the in-session transition behave like a fresh mount. Fall through on
  // error so a failed poker query can't trap the player on the loader forever.
  const needsPokerData = data.currentPlayer.polfEvent.usePokerPhase
  if (needsPokerData && !pokerData && !pokerError) {
    return (
      <main className="min-h-dvh flex items-center justify-center">
        <p className="text-ink-500">Laster…</p>
      </main>
    )
  }

  const player = data.currentPlayer
  const pokerView = pokerData?.currentPlayerPokerView
  const pokerStatus = pokerView?.status ?? null
  // Show the "Premier" tab only when the event actually has payouts configured.
  const hasPayouts = !!(pokerView?.usePayouts && pokerView.payouts.length > 0)

  // Phase: POKER iff the event is FINISHED AND a poker phase is past
  // NOT_STARTED. Until the dealer presses "draw seats" the player still
  // sees Golf tabs (Score / Scorecard / etc.) even on a FINISHED event.
  const phase: Phase =
    player.polfEvent.status === PolfEventStatusEnum.Finished &&
    player.polfEvent.usePokerPhase &&
    pokerStatus !== null &&
    pokerStatus !== PolfEventPokerStatusEnum.NotStarted
      ? 'POKER'
      : 'GOLF'

  // Hard phase redirect. Score entry is golf-only (meaningless once poker is
  // live), and the poker seat / stack views are poker-only. A deep link that
  // lands in the wrong phase — e.g. the golf SMS pointing at /score after the
  // dealer has drawn seats — is bounced to the active phase's home so the
  // content matches the nav (the nav already follows `phase`). Read-only golf
  // archive (scorecard / leaderboard / sidegames) stays reachable during poker
  // by design, so it is intentionally NOT redirected.
  //
  // The phase-exclusive routes below (`score` for golf, `poker`/`stack` for
  // poker) mirror BottomNav's per-phase tab sets — keep the two in sync when
  // adding a route that only belongs to one phase.
  const sub = location.pathname.split('/').filter(Boolean).pop()
  let redirectTo: string | null = null
  if (phase === 'POKER' && sub === 'score') {
    redirectTo = `/s/${playerKey}/poker`
  } else if (phase === 'GOLF' && (sub === 'poker' || sub === 'stack')) {
    redirectTo = `/s/${playerKey}/score`
  }

  // Status pill: derived from event/poker state, not from phase alone.
  let statusLabel: 'LIVE' | 'POKER' | 'FERDIG' | 'PAUSE'
  if (phase === 'POKER') {
    if (pokerStatus === PolfEventPokerStatusEnum.Paused) statusLabel = 'PAUSE'
    else if (pokerStatus === PolfEventPokerStatusEnum.Finished)
      statusLabel = 'FERDIG'
    else statusLabel = 'POKER'
  } else if (player.polfEvent.status === PolfEventStatusEnum.Started) {
    statusLabel = 'LIVE'
  } else {
    statusLabel = 'FERDIG'
  }

  return (
    <div className="min-h-dvh flex flex-col bg-cream-bg">
      <TopBar
        eventName={player.polfEvent.name}
        playerName={player.name}
        hcp={player.hcp}
        modeLabel={modeEyebrow(player.polfEvent.mode)}
        statusLabel={statusLabel}
        offline={!online}
      />
      <main
        className="flex-1 pb-bottom-nav"
        style={{
          paddingBottom: `calc(var(--bottom-nav-h) + env(safe-area-inset-bottom))`,
        }}
      >
        {redirectTo ? (
          <Navigate to={redirectTo} replace />
        ) : (
          <Outlet
            context={
              { player, phase } satisfies {
                player: CurrentPlayerCtx
                phase: Phase
              }
            }
          />
        )}
      </main>
      <UpdatePrompt />
      <BottomNav phase={phase} showPremier={hasPayouts} />
    </div>
  )
}

export default PlayerShell
